import { Divider, Table } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerText from '../../../components/custom_components/Text'
import { SortOrder } from 'antd/lib/table'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import useCompleteContractEmployeeLevelHistory from '../../../graphql/contractHistory/hooks/useCompleteContractEmployeeLevelHistory'
import { useDateTime } from '../../../shared/useDateTime'
import { ContractEmployeeLevelDto } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'

interface Props {
  employeeId: string
  noDivider?: boolean
  resigned?: boolean
  timezone: TimeZoneAndLocale
}

export const LevelHistoryTable: FC<Props> = ({ employeeId, noDivider = false, timezone, resigned }) => {
  const { formatDateShort } = useDateTime(timezone)
  const { t } = useTranslation()

  // load Data
  const { data, loading, error } = useCompleteContractEmployeeLevelHistory(employeeId, resigned)

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const levelHistory: ContractEmployeeLevelDto[] = data.contractEmployeeLevelsByEmployeeId.contractEmployeeLevelHistory
  const showLevelHistory = !!levelHistory && levelHistory.length > 0

  const bonusColumns = [
    {
      key: 'levelHistory-level',
      title: t('profile.contract.levelHistory.level'),
      className: 'minWidth-column-xl',
      sorter: (levelA: ContractEmployeeLevelDto, levelB: ContractEmployeeLevelDto) => {
        const a = levelA.level
        const b = levelB.level
        return a.localeCompare(b)
      },
      render: (tableData: ContractEmployeeLevelDto) => t(`employee.contract.level.${tableData.level}`),
    },
    {
      key: 'levelHistory-fromDate',
      title: t('profile.contract.levelHistory.fromDate'),
      className: 'minWidth-column-s',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA: ContractEmployeeLevelDto, dateB: ContractEmployeeLevelDto) => {
        const a = dateA.fromDate ? dateA.fromDate : ' '
        const b = dateB.fromDate ? dateB.fromDate : ' '
        return a.localeCompare(b)
      },
      render: (tableData: ContractEmployeeLevelDto) => <PerText>{tableData.fromDate && formatDateShort(tableData.fromDate)}</PerText>,
    },
    {
      key: 'levelHistory-toDate',
      title: t('profile.contract.levelHistory.toDate'),
      className: 'minWidth-column-s',
      sorter: (dateA: ContractEmployeeLevelDto, dateB: ContractEmployeeLevelDto) => {
        const a = dateA.toDate ? dateA.toDate : ' '
        const b = dateB.toDate ? dateB.toDate : ' '
        return a.localeCompare(b)
      },
      render: (tableData: ContractEmployeeLevelDto) => <PerText>{tableData.toDate && formatDateShort(tableData.toDate)}</PerText>,
    },
    {
      key: 'levelHistory-note',
      className: 'minWidth-column-xl',
      title: t('profile.contract.levelHistory.note'),
      render: (row: ContractEmployeeLevelDto) => row.note && <PerText>{row.note}</PerText>,
    },
  ]

  return (
    <>
      {showLevelHistory && (
        <>
          {!noDivider && <Divider />}
          <PerText appearance="tile-heading" mb="s">
            {t('profile.contract.levelHistory.header')}
          </PerText>
          <Table
            dataSource={levelHistory.map((currentLevel, index) => ({
              ...currentLevel,
              key: `${index}-${currentLevel.level}`,
            }))}
            className="small"
            columns={bonusColumns}
            pagination={false}
          />
        </>
      )}
    </>
  )
}
